import { ReferenceChip } from '@heidi/agents';
import {
  refTargetFrom,
  unclobberedAttr,
  type RefKind,
  type RefTarget,
  type RefTargetOf,
} from '@heidi/agents/contracts';
import type { ReactNode } from 'react';

import { toReferenceState } from './chat-object-reference-hooks';
import {
  REFERENCE_KINDS,
  useOpenReference,
  type ReferenceFor,
  type ReferenceKind,
} from './chat-object-reference-kinds';

export const ResolveReference = <K extends RefKind>({
  target,
  children,
}: {
  target: RefTargetOf<K>;
  children: (ref: ReferenceFor<K>) => ReactNode;
}): JSX.Element => {
  const { Resolve } = REFERENCE_KINDS[target.kind as K];
  return <Resolve target={target}>{children}</Resolve>;
};

export const ChatObjectReference = ({ target }: { target: RefTarget }): JSX.Element => {
  const openReference = useOpenReference();
  const { open } = REFERENCE_KINDS[target.kind] as ReferenceKind<RefKind>;
  return (
    <ResolveReference target={target}>
      {(ref) => {
        const destination = open?.(target, ref);
        return (
          <ReferenceChip
            kind={target.kind}
            state={toReferenceState(
              ref,
              destination && (() => openReference(destination))
            )}
          />
        );
      }}
    </ResolveReference>
  );
};

type RefMarkdownElementProps = {
  children?: ReactNode;
  'data-kind'?: string;
  dataKind?: string;
  'data-id'?: string;
  dataId?: string;
  'data-session-id'?: string;
  dataSessionId?: string;
};

const targetFromAttrs = (props: RefMarkdownElementProps): RefTarget | null =>
  refTargetFrom(
    unclobberedAttr(props['data-kind'], props.dataKind),
    unclobberedAttr(props['data-id'], props.dataId),
    unclobberedAttr(props['data-session-id'], props.dataSessionId) || undefined
  );

export const RefMarkdownElement = (props: RefMarkdownElementProps): JSX.Element => {
  const target = targetFromAttrs(props);
  return target ? <ChatObjectReference target={target} /> : <>{props.children}</>;
};
